import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getSubmissions } from "../api";
import {
  applyDashboardFilters,
  computeAnalytics,
  toSubmissionRecord,
} from "../dashboard/analytics";

const DashboardDataContext = createContext(null);

export function DashboardDataProvider({ children }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [timeframe, setTimeframe] = useState("30d");
  const [revenue, setRevenue] = useState("all");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getSubmissions();
      const rows = Array.isArray(data?.submissions) ? data.submissions : [];
      setSubmissions(rows.map(toSubmissionRecord));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const revenueOptions = useMemo(() => {
    const set = new Set(submissions.map((s) => s.revenue).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [submissions]);

  const filteredRows = useMemo(
    () => applyDashboardFilters(submissions, { timeframe, revenue }),
    [submissions, timeframe, revenue]
  );

  const analytics = useMemo(() => computeAnalytics(filteredRows), [filteredRows]);

  const value = useMemo(
    () => ({
      submissions: filteredRows,
      allSubmissions: submissions,
      loading,
      error,
      reload: load,
      timeframe,
      setTimeframe,
      revenue,
      setRevenue,
      revenueOptions,
      analytics,
    }),
    [
      filteredRows,
      submissions,
      loading,
      error,
      load,
      timeframe,
      revenue,
      revenueOptions,
      analytics,
    ]
  );

  return (
    <DashboardDataContext.Provider value={value}>{children}</DashboardDataContext.Provider>
  );
}

export function useDashboardData() {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) {
    throw new Error("useDashboardData must be used within DashboardDataProvider");
  }
  return ctx;
}
